import { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { LuCalendar, LuCheck, LuChevronDown, LuDownload, LuLoader } from 'react-icons/lu';

import { Role } from '@ndvr/auth-api';
import { BillingGroupRun, CustodyType, DateStr } from '@ndvr/billing-api';

import { ClientDataTable } from '@/components/table/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Page, PageContent, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { cn } from '@/utils/cn';
import { useUserHasRole } from 'views/admin/dev-queries';
import { useAccountLookup } from 'views/portfolio/account-queries';

import { resolveAccountName } from '../billing-groups/utils';
import { useBillingDateOptions, useBillingGroupRuns, useDownloadOutstandingFeeReport } from './billing-run-queries';
import { BillingRunTableRow, DEFAULT_SETTLEMENT_STATUS_FILTER } from './billing-run-table';
import { BillingGroupHistoryDrawer } from './BillingGroupHistoryDrawer';
import { BillingRunBulkActions } from './BillingRunBulkActions';
import { BillingRunSummaryCards } from './BillingRunSummaryCards';
import { ManualFeeAmendmentDialog } from './ManualFeeAmendmentDialog';
import { useBillingRunColumns } from './useBillingRunColumns';
import { runSelectionBlockReason, useBillingRunSelection } from './useBillingRunSelection';
import { useBillingRunSummary } from './useBillingRunSummary';
import { billingDateLabel } from './utils';

const BROKER_OPTIONS = [
    { custodyType: CustodyType.FIDELITY, label: 'Fidelity' },
    { custodyType: CustodyType.SCHWAB, label: 'Schwab' },
    { custodyType: CustodyType.IB, label: 'IB' },
];

export const BillingRunsPage = () => {
    const { options: billingDateOptions, defaultDate } = useBillingDateOptions();
    const [selectedBillingDate, setSelectedBillingDate] = useState<DateStr>();
    const [historyRun, setHistoryRun] = useState<BillingGroupRun>();
    const [amendmentRun, setAmendmentRun] = useState<BillingGroupRun>();
    const billingDate = selectedBillingDate ?? defaultDate;
    // Historical runs do not preserve the cash position needed for accurate coverage figures.
    const isPastBillingDate = defaultDate != null && billingDate < defaultDate;
    const isCurrentBillingDate = billingDate === defaultDate;
    const isUpcomingBillingDate = billingDateOptions.find((option) => option.date === billingDate)?.isUpcoming ?? false;

    const { data: runs = [], isLoading } = useBillingGroupRuns(billingDate, undefined, isUpcomingBillingDate);
    const { data: isBillingAdmin = false } = useUserHasRole(Role.CHIEF_COMPLIANCE_OFFICER);
    const downloadOutstandingFeeReport = useDownloadOutstandingFeeReport();

    const getSelectionBlockReason = useCallback(
        (run: BillingGroupRun) => runSelectionBlockReason(run, isBillingAdmin),
        [isBillingAdmin],
    );
    const canSelectRun = useCallback(
        (run: BillingGroupRun) => getSelectionBlockReason(run) == null,
        [getSelectionBlockReason],
    );
    const selection = useBillingRunSelection(billingDate, runs, canSelectRun);
    const { clearSelection } = selection;
    const defaultColumnFilters = useMemo<ColumnFiltersState>(
        () => (isUpcomingBillingDate ? [] : [{ id: 'settlementStatus', value: DEFAULT_SETTLEMENT_STATUS_FILTER }]),
        [isUpcomingBillingDate],
    );
    const [columnFilterState, setColumnFilterState] = useState<{
        billingDate: DateStr;
        filters: ColumnFiltersState;
    }>(() => ({ billingDate, filters: defaultColumnFilters }));
    const columnFilters =
        columnFilterState.billingDate === billingDate ? columnFilterState.filters : defaultColumnFilters;
    const handleColumnFiltersChange = useCallback<OnChangeFn<ColumnFiltersState>>(
        (updater) => {
            setColumnFilterState({
                billingDate,
                filters: typeof updater === 'function' ? updater(columnFilters) : updater,
            });
            clearSelection();
        },
        [billingDate, clearSelection, columnFilters],
    );
    const accountGUIDs = useMemo(
        () => [
            ...new Set(
                runs.flatMap((run) => run.accountFees.flatMap((fee) => [fee.accountGUID, fee.settlementAccountGUID])),
            ),
        ],
        [runs],
    );
    const { data: accountLookups = [] } = useAccountLookup(accountGUIDs);
    const accountNameByGUID = useMemo(
        () => new Map(accountLookups.map((lookup) => [lookup.accountGUID, resolveAccountName(lookup)])),
        [accountLookups],
    );

    const tableData = useMemo<BillingRunTableRow[]>(
        () =>
            runs.map((run) => ({
                id: run.billingGroupRunGUID,
                kind: 'group',
                run,
                rollup: run,
                subRows: run.accountFees.map((fee) => ({
                    id: `${run.billingGroupRunGUID}-${fee.accountGUID}`,
                    kind: 'account',
                    run,
                    fee,
                    rollup: fee,
                    accountName: accountNameByGUID.get(fee.accountGUID) ?? fee.accountGUID,
                    subRows: fee.feeDetails.map((detail, index) => ({
                        id: `${run.billingGroupRunGUID}-${fee.accountGUID}-${detail.feeType}-${index}`,
                        kind: 'detail',
                        run,
                        fee,
                        detail,
                    })),
                })),
            })),
        [accountNameByGUID, runs],
    );

    const summary = useBillingRunSummary(runs);
    const columns = useBillingRunColumns({
        accountNameByGUID,
        isPastBillingDate,
        isUpcomingBillingDate,
        selectedRunGUIDs: selection.selectedRunGUIDs,
        getSelectionBlockReason,
        onRunsSelectionChange: selection.setRunsSelected,
        onViewHistory: setHistoryRun,
        onAmendFees: setAmendmentRun,
    });

    return (
        <Page>
            <PageTopBar />
            <PageTitleRow
                title="Billing Runs"
                actions={
                    <div className="flex items-center gap-2">
                        {isCurrentBillingDate && isBillingAdmin && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="outline2"
                                        size="sm"
                                        className="gap-2 px-3"
                                        disabled={downloadOutstandingFeeReport.isPending}
                                    >
                                        {downloadOutstandingFeeReport.isPending ? (
                                            <LuLoader className="text-muted-foreground h-4 w-4 animate-spin" />
                                        ) : (
                                            <LuDownload className="text-muted-foreground h-4 w-4" />
                                        )}
                                        Download broker files
                                        <LuChevronDown className="text-muted-foreground h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {BROKER_OPTIONS.map(({ custodyType, label }) => (
                                        <DropdownMenuItem
                                            key={custodyType}
                                            onClick={() => downloadOutstandingFeeReport.mutate(custodyType)}
                                        >
                                            {label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline2" size="sm" className="gap-2 px-3">
                                    <LuCalendar className="text-muted-foreground h-4 w-4" />
                                    {billingDateLabel(billingDate)}
                                    <LuChevronDown className="text-muted-foreground h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                                {billingDateOptions.map((option) => (
                                    <DropdownMenuItem
                                        key={option.date}
                                        className="flex items-center justify-between gap-2"
                                        onClick={() => setSelectedBillingDate(option.date)}
                                    >
                                        <span>{billingDateLabel(option.date)}</span>
                                        <span className="flex items-center gap-2">
                                            {option.isCurrent && <Badge variant="success">Current</Badge>}
                                            {option.isUpcoming && <Badge variant="secondary">Upcoming</Badge>}
                                            {option.date === billingDate && (
                                                <LuCheck className="text-muted-foreground h-4 w-4" />
                                            )}
                                        </span>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                }
            />

            <PageContent>
                {!isUpcomingBillingDate && (
                    <BillingRunSummaryCards summary={summary} showCashCoverage={!isPastBillingDate} />
                )}

                <ClientDataTable
                    className={cn(
                        selection.selectedRuns.length > 0 && 'pb-18',
                        isUpcomingBillingDate
                            ? 'min-w-[720px] min-[1800px]:min-w-[900px]'
                            : 'min-w-[960px] min-[1800px]:min-w-[1400px]',
                        'overflow-hidden rounded-xl border border-gray-200',
                    )}
                    truncateCells
                    data={tableData}
                    columns={columns}
                    isLoading={isLoading}
                    emptyMessage={
                        isUpcomingBillingDate
                            ? 'No upcoming billing runs found for this date.'
                            : 'No billing runs found for this date.'
                    }
                    initialState={{
                        sorting: [
                            { id: 'billableNav', desc: true },
                            { id: 'billingGroupName', desc: false },
                        ],
                    }}
                    state={{ columnFilters }}
                    onColumnFiltersChange={handleColumnFiltersChange}
                    rowClassName={(item, row) =>
                        item.kind === 'group' && row.getIsExpanded()
                            ? 'bg-gray-50 hover:bg-gray-50'
                            : item.kind === 'account'
                              ? 'bg-gray-50 hover:bg-gray-50'
                              : ''
                    }
                    getRowCanExpand={(row) => !!row.original.subRows?.length}
                    getSubRows={(row) => row.subRows}
                />
            </PageContent>

            <BillingRunBulkActions billingDate={billingDate} canRerun={isBillingAdmin} selection={selection} />

            {historyRun && <BillingGroupHistoryDrawer run={historyRun} onClose={() => setHistoryRun(undefined)} />}
            {amendmentRun && (
                <ManualFeeAmendmentDialog
                    key={amendmentRun.billingGroupRunGUID}
                    run={amendmentRun}
                    accountNameByGUID={accountNameByGUID}
                    onClose={() => setAmendmentRun(undefined)}
                />
            )}
        </Page>
    );
};
